import type { ComponentProps, ReactNode } from 'react'

import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export const Dialog = (props: DialogPrimitive.Root.Props): ReactNode => {
    return <DialogPrimitive.Root data-slot='dialog' {...props} />
}

export const DialogTrigger = (props: DialogPrimitive.Trigger.Props): ReactNode => {
    return <DialogPrimitive.Trigger data-slot='dialog-trigger' {...props} />
}

export const DialogPortal = (props: DialogPrimitive.Portal.Props): ReactNode => {
    return <DialogPrimitive.Portal data-slot='dialog-portal' {...props} />
}

export const DialogClose = (props: DialogPrimitive.Close.Props): ReactNode => {
    return <DialogPrimitive.Close data-slot='dialog-close' {...props} />
}

export const DialogOverlay = ({ className, ...props }: DialogPrimitive.Backdrop.Props): ReactNode => {
    return (
        <DialogPrimitive.Backdrop
            data-slot='dialog-overlay'
            className={cn(
                'fixed inset-0 isolate z-50 bg-ink/45 duration-200 supports-backdrop-filter:backdrop-blur-[3px] data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
                className
            )}
            {...props}
        />
    )
}

type DialogContentProps = DialogPrimitive.Popup.Props & {
    showCloseButton?: boolean
}

export const DialogContent = ({
    className,
    children,
    showCloseButton = true,
    ...props
}: DialogContentProps): ReactNode => {
    return (
        <DialogPortal>
            <DialogOverlay />

            <DialogPrimitive.Popup
                data-slot='dialog-content'
                className={cn(
                    'fixed top-1/2 left-1/2 z-50 grid w-[calc(100%-1.5rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 rounded-3xl bg-popover p-5 text-sm text-popover-foreground shadow-2xl ring-1 ring-ink/5 duration-200 outline-none sm:p-6 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95',
                    className
                )}
                {...props}
            >
                {children}

                {showCloseButton && (
                    <DialogPrimitive.Close
                        data-slot='dialog-close'
                        className='absolute top-3 right-3 inline-flex size-9 items-center justify-center rounded-full text-muted-foreground transition-colors outline-none hover:bg-muted hover:text-ink focus-visible:ring-4 focus-visible:ring-ring/30'
                    >
                        <X className='size-5' aria-hidden='true' />
                        <span className='sr-only'>
                            Tutup
                        </span>
                    </DialogPrimitive.Close>
                )}
            </DialogPrimitive.Popup>
        </DialogPortal>
    )
}

export const DialogHeader = ({ className, ...props }: ComponentProps<'div'>): ReactNode => {
    return (
        <div
            data-slot='dialog-header'
            className={cn('flex flex-col gap-1.5', className)}
            {...props}
        />
    )
}

export const DialogFooter = ({ className, ...props }: ComponentProps<'div'>): ReactNode => {
    return (
        <div
            data-slot='dialog-footer'
            className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
            {...props}
        />
    )
}

export const DialogTitle = ({ className, ...props }: DialogPrimitive.Title.Props): ReactNode => {
    return (
        <DialogPrimitive.Title
            data-slot='dialog-title'
            className={cn('font-heading text-lg font-bold tracking-tight text-ink', className)}
            {...props}
        />
    )
}

export const DialogDescription = ({ className, ...props }: DialogPrimitive.Description.Props): ReactNode => {
    return (
        <DialogPrimitive.Description
            data-slot='dialog-description'
            className={cn('text-sm leading-relaxed text-muted-foreground', className)}
            {...props}
        />
    )
}
